import { Component, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  AppBarCloseActionService,
  AppBarTitleService,
  BrandingService,
  CompanyBranding,
} from 'common';
import {
  PasswordChangeFormGroup,
  PasswordChangeFormValue,
  PasswordChangeRequest,
  QueryParamsPasswordChangeFormComponent
} from '../../model/password-change.model';
import { PasswordChangeService } from '../../service/password-change.service';

@UntilDestroy()
@Component({
    selector: 'ifs-password-change-form',
    templateUrl: './password-change-form.component.html',
    styleUrls: ['./password-change-form.component.scss'],
    standalone: false
})
export class PasswordChangeFormComponent implements OnInit {
  params: QueryParamsPasswordChangeFormComponent;

  form: PasswordChangeFormGroup;
  branding: CompanyBranding;

  constructor(
    private route: ActivatedRoute,
    private passwordChangeService: PasswordChangeService,
    private formBuilder: UntypedFormBuilder,
    private appBarTitleService: AppBarTitleService,
    private appBarCloseService: AppBarCloseActionService,
    private brandingService: BrandingService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.route.queryParams
      .pipe(untilDestroyed(this))
      .subscribe(this.paramsInit.bind(this));

    this.brandingService.getBranding()
      .subscribe((branding) => { this.branding = branding });

    this.appBarCloseService.closing$
      .pipe(untilDestroyed(this))
      .subscribe(this.handleClose.bind(this));

    this.appBarTitleService.title = 'Update your password';
  }

  private initForm(): void {
    this.form = this.formBuilder.group(
      {
        currentPassword: ['', Validators.compose([Validators.required])],
        newPassword: [
          '',
          Validators.compose([
            Validators.required,
            Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/),
          ]),
        ],
        confirmPassword: ['', Validators.required],
      },
      {
        validator: formPasswordConfirmationValidator(
          'newPassword',
          'confirmPassword'
        ),
      }
    ) as PasswordChangeFormGroup;
  }

  submit(): void {
    if (!this.form.valid) return;

    this.passwordChangeService
      .submit({
        ...this.formToRequest(this.form.value),
        returnUrl: this.params?.redirectUrl ?? this.branding?.portalUrl
      })
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  back(): void {
    this.handleClose();
  }

  private paramsInit(params: QueryParamsPasswordChangeFormComponent): void {
    this.params = params;
  }

  private handleClose(): void {
    window.location.href = this.params?.redirectUrl ?? this.branding?.portalUrl;
  }

  private formToRequest(value: PasswordChangeFormValue): PasswordChangeRequest {
    return {
      oldPassword: value.currentPassword,
      newPassword: value.newPassword
    };
  }
}

function formPasswordConfirmationValidator(
  controlName: string,
  matchingControlName: string
) {
  return (formGroup: UntypedFormGroup) => {
    const control = formGroup.controls[controlName];
    const matchingControl = formGroup.controls[matchingControlName];

    if (matchingControl.errors) {
      return;
    }

    if (control.value !== matchingControl.value) {
      matchingControl.setErrors({ mustMatch: true });
    } else {
      matchingControl.setErrors(null);
    }
  };
}
